/**
 * T13: the API returns at most a fixed number of rows per request (1,000 on this project) and says nothing when it
 * stops — a 1,500-room event would silently lose a third of its rooms. Every list that can be large is read
 * through here: exact count first, then stable, ordered pages until every row has arrived.
 */
import type { PostgrestError } from '@supabase/supabase-js';

export const PAGE_ROWS = 1000;

type Page<T> = PromiseLike<{ data: T[] | null; error: PostgrestError | null; count: number | null }>;

/**
 * `page(from, to)` must select with `{ count: 'exact' }`, have a total order (end with `.order('id')`) and apply
 * `.range(from, to)`. Returns every row, or the first error with the rows read so far.
 */
export async function fetchAll<T>(page: (from: number, to: number) => Page<T>): Promise<{ data: T[]; error: PostgrestError | null }> {
  const out: T[] = [];
  let total: number | null = null;
  for (let from = 0; ; ) {
    const { data, error, count } = await page(from, from + PAGE_ROWS - 1);
    if (error) return { data: out, error };
    const rows = data ?? [];
    out.push(...rows);
    total ??= count;
    from += rows.length;
    // stop when everything counted has arrived, or the server has nothing more (a count can be null)
    if (!rows.length || (total !== null ? out.length >= total : rows.length < PAGE_ROWS)) return { data: out, error: null };
  }
}
